import { z } from 'zod';

// Base user schema for form validation
export const userFormSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  
  email: z.string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required')
    .max(255, 'Email must be less than 255 characters')
    .toLowerCase(),
  
  roles: z.array(z.string())
    .min(1, 'At least one role must be selected')
    .max(10, 'Maximum 10 roles allowed'),
  
  organizationId: z.string()
    .optional()
    .or(z.literal('')),
  
  isActive: z.boolean()
    .default(true),
  

});

// Schema for creating new users (excludes ID, includes generated password info)
export const createUserSchema = userFormSchema.extend({
  // Password will be auto-generated, so we don't include it in the form
  generatePassword: z.boolean().default(true)
});

// Schema for updating existing users (all fields optional except ID)
export const updateUserSchema = userFormSchema.partial().extend({
  id: z.string().uuid('Invalid user ID')
});

// Type exports for TypeScript
export type UserFormData = z.infer<typeof userFormSchema>;
export type CreateUserData = z.infer<typeof createUserSchema>;
export type UpdateUserData = z.infer<typeof updateUserSchema>;

// Helper schema for role validation based on user permissions
export const createRoleValidationSchema = (availableRoles: string[]) => {
  return z.array(z.string().refine(
    (role) => availableRoles.includes(role),
    { message: 'Invalid role selected' }
  )).min(1, 'At least one role must be selected');
};

// Helper for organization validation
export const createOrganizationValidationSchema = (availableOrgs: Array<{id: string, name: string}>) => {
  const orgIds = availableOrgs.map(org => org.id);
  return z.string().refine(
    (orgId) => orgIds.includes(orgId),
    { message: 'Invalid organization selected' }
  ).optional().nullable();
}; 